import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Inbox,
    FileText,
    Briefcase,
    CalendarDays,
    Receipt,
    Users as UsersIcon,
    UserCheck,
    BookOpen,
    GraduationCap,
    Settings as SettingsIcon,
    Upload as UploadIcon,
    LogOut,
    Menu,
    X,
    Phone,
    Bot,
    Gauge,
} from "lucide-react";
import EkgLine from "@/components/EkgLine";
import { useAuth } from "@/lib/auth";

const NAV = [
    { to: "/agent", label: "Jeff (Agent)", icon: Bot, testId: "nav-agent" },
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
    { to: "/leads", label: "Leads", icon: Inbox, testId: "nav-leads" },
    { to: "/estimates", label: "Estimates", icon: FileText, testId: "nav-estimates" },
    { to: "/jobs", label: "Jobs", icon: Briefcase, testId: "nav-jobs" },
    { to: "/schedule", label: "Schedule", icon: CalendarDays, testId: "nav-schedule" },
    { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices" },
    { to: "/team", label: "Team", icon: UsersIcon, testId: "nav-team", roles: ["admin", "supervisor"] },
    { to: "/training", label: "Training", icon: GraduationCap, testId: "nav-training" },
    { to: "/admin/onboarding", label: "Onboarding", icon: UserCheck, testId: "nav-onboarding", roles: ["admin", "supervisor"] },
    { to: "/admin/training", label: "Training admin", icon: GraduationCap, testId: "nav-admin-training", roles: ["admin", "supervisor"] },
    { to: "/admin/bid-intake", label: "Bid intake", icon: UploadIcon, testId: "nav-bid-intake", roles: ["admin", "supervisor"] },
    { to: "/admin/handbook", label: "Handbook", icon: BookOpen, testId: "nav-handbook", roles: ["admin", "supervisor"] },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
    { to: "/usage", label: "Usage", icon: Gauge, testId: "nav-usage", roles: ["admin", "supervisor"] },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const allowed = (item) => !item.roles || item.roles.includes(user?.role);
    const initials = (user?.name || user?.email || "?")
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <div className="min-h-screen flex">
            {/* Sidebar (desktop) */}
            <aside
                data-testid="admin-sidebar"
                className="hidden md:flex w-[260px] shrink-0 flex-col border-r border-[var(--brand-surface)] bg-[var(--brand-white)] sticky top-0 h-screen"
            >
                <div className="px-6 pt-6 pb-4">
                    <div className="logo-plate">
                        <img
                            src="/brand/logo-256.png"
                            alt="CPR Paint & Coatings"
                            data-testid="sidebar-logo"
                            style={{ height: 36, width: "auto", display: "block" }}
                        />
                    </div>
                </div>
                <EkgLine className="px-6" />
                <nav className="flex-1 px-4 py-5 flex flex-col gap-1">
                    <p className="eyebrow px-3 pb-2">Workspace</p>
                    {NAV.filter(allowed).map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testId}
                                className={({ isActive }) =>
                                    `sidebar-link${isActive ? " active" : ""}`
                                }
                            >
                                <Icon size={18} />
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-[var(--brand-surface)]">
                    <div className="flex items-center gap-3 mb-3">
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center font-display font-bold text-sm"
                            style={{
                                background: "rgba(210,5,28,0.1)",
                                color: "var(--brand-red)",
                            }}
                        >
                            {initials}
                        </div>
                        <div className="min-w-0">
                            <p
                                className="font-display font-bold text-sm truncate"
                                data-testid="sidebar-user-name"
                            >
                                {user?.name || user?.email}
                            </p>
                            <p className="text-xs text-muted capitalize">{user?.role}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost w-full justify-start"
                        onClick={handleLogout}
                        data-testid="sidebar-logout-btn"
                    >
                        <LogOut size={16} />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[var(--brand-white)] border-b border-[var(--brand-surface)]">
                <div className="flex items-center justify-between px-4 py-3">
                    <img
                        src="/brand/logo-256.png"
                        alt="CPR Paint"
                        style={{ height: 30, width: "auto" }}
                    />
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="btn btn-ghost"
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
                {open && (
                    <div className="border-t border-[var(--brand-surface)] px-3 py-3 flex flex-col gap-1 bg-[var(--brand-white)]">
                        {NAV.filter(allowed).map((item) => {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.end}
                                    data-testid={`${item.testId}-mobile`}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) =>
                                        `sidebar-link${isActive ? " active" : ""}`
                                    }
                                >
                                    <Icon size={18} />
                                    {item.label}
                                </NavLink>
                            );
                        })}
                        <button
                            type="button"
                            className="sidebar-link"
                            onClick={handleLogout}
                            data-testid="mobile-logout-btn"
                        >
                            <LogOut size={18} />
                            Sign out
                        </button>
                    </div>
                )}
            </div>

            {/* Main */}
            <main className="flex-1 min-w-0 md:ml-0 pt-[64px] md:pt-0">
                <Outlet />
            </main>
        </div>
    );
}
